import { useEffect, useState, type ReactNode } from 'react'
import { NavLink, useNavigate } from 'react-router'
import { useAuth } from '../auth/AuthProvider'
import { NotificationBell } from './NotificationBell'
import { Button, cx } from './ui'

const NAV = [
  { to: '/', label: 'My work', end: true, icon: InboxIcon },
  { to: '/board', label: 'Board', icon: BoardIcon },
  { to: '/projects', label: 'Projects', icon: FolderIcon },
  { to: '/meetings', label: 'Meetings', icon: CalendarIcon },
  { to: '/companies', label: 'Companies', icon: BuildingIcon },
] as const

export function Layout({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'))

  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark)
    localStorage.setItem('apo-theme', dark ? 'dark' : 'light')
  }, [dark])

  const isAdmin = user?.role === 'ADMIN'

  const navItems = [...NAV, ...(isAdmin ? [{ to: '/admin', label: 'Team', icon: UsersIcon }] : [])]

  async function handleSignOut() {
    await signOut()
    navigate('/login', { replace: true })
  }

  return (
    <div className="min-h-full">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-3 sm:px-6">
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="-ml-1 rounded-lg p-2 text-slate-600 hover:bg-slate-100 md:hidden dark:text-slate-400 dark:hover:bg-slate-800"
            aria-label="Toggle navigation"
            aria-expanded={menuOpen}
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
              <path d="M3 5h14v2H3V5Zm0 4h14v2H3V9Zm0 4h14v2H3v-2Z" />
            </svg>
          </button>

          <div className="flex items-center gap-2">
            <span className="grid h-7 w-7 place-items-center rounded-md bg-brand-600 text-xs font-bold text-white">
              A
            </span>
            <span className="font-semibold tracking-tight">Atlas</span>
          </div>

          <nav className="ml-4 hidden items-center gap-1 md:flex">
            {navItems.map((item) => (
              <NavItem key={item.to} {...item} />
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <NotificationBell />

            <button
              type="button"
              onClick={() => setDark((v) => !v)}
              className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
              aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
              title={dark ? 'Light mode' : 'Dark mode'}
            >
              {dark ? <SunIcon /> : <MoonIcon />}
            </button>

            <div className="hidden text-right sm:block">
              <p className="text-sm leading-tight font-medium">{user?.name}</p>
              <p className="text-xs leading-tight text-slate-500 dark:text-slate-400">
                {isAdmin ? 'Administrator' : 'Member'}
              </p>
            </div>

            <Button variant="ghost" size="sm" onClick={handleSignOut}>
              Sign out
            </Button>
          </div>
        </div>

        {menuOpen && (
          <nav className="flex flex-col gap-1 border-t border-slate-200 px-4 py-2 md:hidden dark:border-slate-800">
            {navItems.map((item) => (
              <NavItem key={item.to} {...item} onClick={() => setMenuOpen(false)} />
            ))}
          </nav>
        )}
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8">{children}</main>
    </div>
  )
}

function NavItem({
  to,
  label,
  end,
  icon: Icon,
  onClick,
}: {
  to: string
  label: string
  end?: boolean
  icon: () => ReactNode
  onClick?: () => void
}) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      className={({ isActive }) =>
        cx(
          'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
          isActive
            ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200'
            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100',
        )
      }
    >
      <Icon />
      {label}
    </NavLink>
  )
}

// ---------------------------------------------------------------------------
// Icons — inline so the app ships with no icon dependency and no network calls.
// ---------------------------------------------------------------------------

const iconProps = {
  viewBox: '0 0 20 20',
  fill: 'currentColor',
  className: 'h-4 w-4 shrink-0',
  'aria-hidden': true,
} as const

function InboxIcon() {
  return (
    <svg {...iconProps}>
      <path d="M3 4.5A1.5 1.5 0 0 1 4.5 3h11A1.5 1.5 0 0 1 17 4.5v6h-3.6a1 1 0 0 0-.9.55l-.45.9a1 1 0 0 1-.9.55H8.85a1 1 0 0 1-.9-.55l-.45-.9a1 1 0 0 0-.9-.55H3v-6Zm0 7.5h3.2l.3.6A2 2 0 0 0 8.3 14h3.4a2 2 0 0 0 1.8-1.4l.3-.6H17v3.5A1.5 1.5 0 0 1 15.5 17h-11A1.5 1.5 0 0 1 3 15.5V12Z" />
    </svg>
  )
}

function BoardIcon() {
  return (
    <svg {...iconProps}>
      <path d="M3 4a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4Zm7 0a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1h-3a1 1 0 0 1-1-1V4Z" />
    </svg>
  )
}

function FolderIcon() {
  return (
    <svg {...iconProps}>
      <path d="M2 5.5A1.5 1.5 0 0 1 3.5 4h3.3a1.5 1.5 0 0 1 1.06.44L9 5.5h7.5A1.5 1.5 0 0 1 18 7v7.5a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 2 14.5v-9Z" />
    </svg>
  )
}

function CalendarIcon() {
  return (
    <svg {...iconProps}>
      <path d="M6 2a1 1 0 0 1 1 1v1h6V3a1 1 0 1 1 2 0v1h.5A1.5 1.5 0 0 1 17 5.5V7H3V5.5A1.5 1.5 0 0 1 4.5 4H5V3a1 1 0 0 1 1-1ZM3 8.5h14v7a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 3 15.5v-7Zm2.5 2a.75.75 0 0 0 0 1.5h2a.75.75 0 0 0 0-1.5h-2Zm5 0a.75.75 0 0 0 0 1.5h4a.75.75 0 0 0 0-1.5h-4Zm-5 3a.75.75 0 0 0 0 1.5h2a.75.75 0 0 0 0-1.5h-2Zm5 0a.75.75 0 0 0 0 1.5h4a.75.75 0 0 0 0-1.5h-4Z" />
    </svg>
  )
}

function BuildingIcon() {
  return (
    <svg {...iconProps}>
      <path d="M4 3a1 1 0 0 0-1 1v13h6v-3h2v3h6V7a1 1 0 0 0-1-1h-4V4a1 1 0 0 0-1-1H4Zm1 2h2v2H5V5Zm4 0h2v2H9V5ZM5 8h2v2H5V8Zm4 0h2v2H9V8Zm4 0h2v2h-2V8ZM5 11h2v2H5v-2Zm8 0h2v2h-2v-2Z" />
    </svg>
  )
}

function UsersIcon() {
  return (
    <svg {...iconProps}>
      <path d="M7 8a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Zm6.5 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM2 14.5C2 12 4.2 10 7 10s5 2 5 4.5V16H2v-1.5Zm11.5-3c2 0 4.5 1.3 4.5 3.5V16h-5v-1.5c0-1.1-.4-2.1-1-2.9.5-.1 1-.1 1.5-.1Z" />
    </svg>
  )
}

function SunIcon() {
  return (
    <svg {...iconProps} className="h-5 w-5">
      <path d="M10 4a1 1 0 0 1-1-1V2a1 1 0 0 1 2 0v1a1 1 0 0 1-1 1Zm0 12a1 1 0 0 1 1 1v1a1 1 0 0 1-2 0v-1a1 1 0 0 1 1-1Zm7-6a1 1 0 0 1 1 1h1a1 1 0 0 1 0-2h-1a1 1 0 0 1-1 1ZM2 10a1 1 0 0 1 1-1h1a1 1 0 0 1 0 2H3a1 1 0 0 1-1-1Zm12.2-4.2a1 1 0 0 1 0-1.4l.7-.7a1 1 0 0 1 1.4 1.4l-.7.7a1 1 0 0 1-1.4 0Zm-9.8 8.4a1 1 0 0 1 1.4 0 1 1 0 0 1 0 1.4l-.7.7a1 1 0 0 1-1.4-1.4l.7-.7Zm11.2.7a1 1 0 0 1-1.4 1.4l-.7-.7a1 1 0 0 1 1.4-1.4l.7.7ZM5.8 5.8a1 1 0 0 1-1.4 0l-.7-.7a1 1 0 0 1 1.4-1.4l.7.7a1 1 0 0 1 0 1.4ZM10 6.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7Z" />
    </svg>
  )
}

function MoonIcon() {
  return (
    <svg {...iconProps} className="h-5 w-5">
      <path d="M9.5 2a8 8 0 1 0 8.4 10.6.75.75 0 0 0-1-.9A6 6 0 0 1 8.3 3.5a.75.75 0 0 0-.4-1.3A8 8 0 0 0 9.5 2Z" />
    </svg>
  )
}
